import { createSlice } from "@reduxjs/toolkit";

const emptyMessage = {
  message: "",
  type: null,
  longText: "",
  helpLink: "",
  id: null,
};

const MAX_TOASTS = 4;

const normalizeType = (type) => {
  const allowed = ["success", "error", "warning", "info"];
  if (typeof type === "string" && allowed.includes(type)) {
    return type;
  }
  return null;
};

const inferType = (text, explicitType) => {
  const normalized = normalizeType(explicitType);
  if (normalized) {
    return normalized;
  }

  const t = String(text || "").toLowerCase();
  if (
    /error|fail|invalid|unable|could not|not found|unauthorized|denied|missing/.test(
      t
    )
  ) {
    return "error";
  }
  if (/warn/.test(t)) {
    return "warning";
  }
  if (
    /success|updated|created|added|deleted|started|saved|complete|activated|deactivated/.test(
      t
    )
  ) {
    return "success";
  }
  return "info";
};

/** Build a toast payload from any historical setMessage call shape. */
export const buildToastPayload = (messageOrObj, type) => {
  let text = "";
  let explicitType = type;
  let longText = "";
  let helpLink = "";

  if (messageOrObj && typeof messageOrObj === "object") {
    text = messageOrObj.message ?? messageOrObj.msg ?? "";
    explicitType = messageOrObj.type ?? type;
    longText = messageOrObj.longText || "";
    helpLink = messageOrObj.helpLink || "";
  } else if (messageOrObj != null) {
    text = String(messageOrObj);
  }

  text = String(text || "").trim();
  if (!text) {
    return null;
  }

  return {
    message: text,
    type: inferType(text, explicitType),
    longText,
    helpLink,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  };
};

const initialState = {
  message: emptyMessage,
  toasts: [],
};

const messageSlice = createSlice({
  name: "message",
  initialState,
  reducers: {
    setMessage: {
      reducer: (state, action) => {
        const toast = action.payload;
        if (!toast || !toast.message) {
          return;
        }
        state.message = toast;
        state.toasts.push(toast);
        if (state.toasts.length > MAX_TOASTS) {
          state.toasts = state.toasts.slice(-MAX_TOASTS);
        }
      },
      // Supports:
      //   setMessage("Saved")
      //   setMessage("Saved", "success")
      //   setMessage({ message: "Saved", type: "success" })
      prepare: (messageOrObj, type) => {
        const payload = buildToastPayload(messageOrObj, type);
        return { payload };
      },
    },
    dismissToast: (state, action) => {
      const id = action.payload;
      state.toasts = state.toasts.filter((toast) => toast.id !== id);
      if (state.message?.id === id) {
        state.message = emptyMessage;
      }
    },
    clearMessage: (state) => {
      state.message = emptyMessage;
      state.toasts = [];
    },
    setPopupMessage: {
      reducer: (state, action) => {
        const toast = action.payload;
        if (!toast || !toast.message) {
          return;
        }
        state.message = toast;
        state.toasts.push(toast);
        if (state.toasts.length > MAX_TOASTS) {
          state.toasts = state.toasts.slice(-MAX_TOASTS);
        }
      },
      prepare: (messageOrObj, type) => {
        const payload = buildToastPayload(messageOrObj, type);
        return { payload };
      },
    },
    clearPopupMessage: (state) => {
      state.message = emptyMessage;
      state.toasts = [];
    },
  },
});

const { reducer, actions } = messageSlice;

export const {
  setMessage,
  clearMessage,
  dismissToast,
  setPopupMessage,
  clearPopupMessage,
} = actions;

export default reducer;
